import React, { useEffect, useState, useRef, memo } from 'react';
import { useParams } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useKitchenStations } from '../../lib/admin-hooks';
import { useMenuCategories, useMenuItems, useMenuItemOptions } from '../../lib/menu-hooks';
import { supabase } from '../../lib/supabase';
import { Skeleton } from '../../components/ui/Skeleton';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Input, Textarea } from '../../components/ui/Input';
import { Modal } from '../../components/ui/Modal';
import { EmptyState } from '../../components/ui/EmptyState';
import { UtensilsCrossed, Plus, Edit2, Trash2, GripVertical, Layers, Settings2, ImageIcon, Upload, X, Search, ListOrdered, FolderTree, Printer, Check } from 'lucide-react';
import { MenuReorderDrawer } from '../../components/admin/MenuReorderDrawer';
import { CategoryReorderDrawer } from '../../components/admin/CategoryReorderDrawer';
import { cn, formatCurrency } from '../../lib/utils';
import { MenuCategory, MenuItem, MenuItemOption } from '../../lib/types';
export function MenuManagementPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = (() => {
    try {
      const {
        useTenant
      } = require('../../lib/tenant-context');
      return useTenant();
    } catch {
      return null;
    }
  })();
  const {
    categories,
    loading: catLoading,
    createCategory,
    updateCategory,
    toggleCategory
  } = useMenuCategories(restaurant?.id);
  const {
    items,
    loading: itemsLoading,
    createItem,
    updateItem,
    toggleItem,
    deleteItem
  } = useMenuItems(restaurant?.id);
  const {
    stations: rawStations
  } = useKitchenStations(restaurant?.id);
  // NatuCafé Coffee Shop (id 19) — el dueño no usa las estaciones
  // "Pizzeria" ni "Barra" en este local; sólo Cocina y Postres y
  // Batidos. Las filtramos del dropdown del modal de producto para
  // evitar que se asigne accidentalmente. Solo aplica para NatuCafé,
  // el resto de restaurantes ve todas sus estaciones intactas.
  const isNatuCafe = String(restaurant?.id) === '19' || restaurant?.slug === 'natucafe-coffee-shop';
  const stations = isNatuCafe ? (rawStations || []).filter((s: any) => s.code !== 'pizzeria' && s.code !== 'barra' && s.code !== 'PIZZERIA' && s.code !== 'BARRA') : rawStations;
  const [activeTab, setActiveTab] = useState<'categories' | 'items'>('items');
  // Modals state
  const [isCatModalOpen, setIsCatModalOpen] = useState(false);
  const [editingCat, setEditingCat] = useState<MenuCategory | null>(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | null>(null);
  const [isOptionsModalOpen, setIsOptionsModalOpen] = useState(false);
  const [managingOptionsForItem, setManagingOptionsForItem] = useState<MenuItem | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategoryId, setFilterCategoryId] = useState<number | ''>('');
  const [isReorderDrawerOpen, setIsReorderDrawerOpen] = useState(false);
  const [isCatReorderDrawerOpen, setIsCatReorderDrawerOpen] = useState(false);
  if (restLoading || !restaurant) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-id="element-5069">
        <Skeleton className="w-16 h-16 rounded-full" data-id="element-5070" />
      </div>;
  }
  const handleOpenCatModal = (cat?: MenuCategory) => {
    setEditingCat(cat || null);
    setIsCatModalOpen(true);
  };
  const handleOpenItemModal = (item?: MenuItem) => {
    setEditingItem(item || null);
    setIsItemModalOpen(true);
  };
  const handleOpenOptionsModal = (item: MenuItem) => {
    setManagingOptionsForItem(item);
    setIsOptionsModalOpen(true);
  };
  // ─── Imprimir menú ─────────────────────────────────────────────────
  // Abre una ventana nueva con un layout listo para imprimir o
  // exportar a PDF. Soporta dos variantes:
  //
  //   1. Default — limpio, en serif, blanco y negro con acento ámbar
  //      (para cualquier restaurante).
  //   2. NatuCafé branded — fondo oscuro #0F0D0B, acentos dorados
  //      #C6A969, hero con cover_url, logo, fotos de productos y
  //      tipografía editorial. Coincide con el menú digital.
  //
  // Nota técnica: las tags HTML especiales (`<style>`, `<script>`)
  // se arman partidas en pedazos porque el auto-formatter del editor
  // intenta reformatear cualquier `<style>` literal en el código (lo
  // confunde con JSX) y rompe el build con "Expected }".
  const handlePrintMenu = async () => {
    // Tags partidas para evitar que el auto-formatter las "JSX-ifique"
    const T_STYLE_O = '<' + 'style>';
    const T_STYLE_C = '</' + 'style>';
    const T_SCRIPT_O = '<' + 'script>';
    const T_SCRIPT_C = '</' + 'script>';
    const escape = (s: string | null | undefined) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    // ─── Orden de categorías (idéntico al menú digital + waiter) ─────
    // Reglas:
    //   - Restaurante normal → categorías activas ordenadas por
    //     `sort_order` ascendente. Es el mismo orden que ya usan
    //     `useMenuCategories` (Supabase ORDER BY sort_order ASC) y el
    //     `activeCategories` del waiter.
    //   - NatuCafé (id 19 / slug natucafe-coffee-shop) → mismo pipeline
    //     que `WaiterPOSPage.activeCategories`: primero el grupo
    //     "Bebidas y Repostería" (Bebidas calientes → Bebidas frías →
    //     Té y más → Batidos → Repostería) y luego "Comida", cada
    //     grupo internamente por DB `sort_order`. Coincide con la
    //     agrupación `NATUCAFE_PARENT_CATEGORIES` del menú público.
    //     También escondemos los placeholders "Bebidas y Repostería"
    //     y "Comida" porque sus items reales viven en las subcats
    //     (mismo criterio que waiter).
    const getNatuCafeParent = (name: string): 'bebidas' | 'comida' => {
      const n = (name || '').toLowerCase().trim();
      const bebidasSubs = ['bebidas calientes', 'bebidas frías', 'bebidas frias', 'té y más', 'te y mas', 'aguadulce', 'batidos', 'batidos en agua', 'batidos con leche', 'postres', 'repostería', 'reposteria'];
      if (bebidasSubs.some(sub => n.includes(sub) || sub.includes(n))) {
        return 'bebidas';
      }
      return 'comida';
    };
    const hiddenPrintCategoryNames = isNatuCafe ? new Set(['bebidas y reposteria', 'bebidas y repostería', 'comida'].map(s => s.toLowerCase())) : new Set<string>();
    const filteredCats = categories.filter(c => c.is_active).filter(c => !hiddenPrintCategoryNames.has((c.name || '').trim().toLowerCase()));
    const activeCats = isNatuCafe ? (() => {
      const bebidas = filteredCats.filter(c => getNatuCafeParent(c.name || '') === 'bebidas').sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      const comida = filteredCats.filter(c => getNatuCafeParent(c.name || '') === 'comida').sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
      return [...bebidas, ...comida];
    })() : filteredCats.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    const today = new Date().toLocaleDateString('es-CR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    // ─── Fetch ALL menu_item_options for this restaurant in ONE query ─
    // El menú impreso debe mostrar exactamente la misma info que el
    // menú digital: nombre + descripción + precio base + TODOS los
    // option_groups con sus opciones (Requerido / Elegí hasta N) y los
    // price_delta. Reutilizamos el mismo modelo de datos que usa
    // ProductDetailModal (menu_item_options), pero con un solo fetch
    // bulk para evitar N+1.
    //
    // Estructura:
    //   optionsByItem: Map<menu_item_id, {
    //     groupName -> { options: MenuItemOption[], firstSort: number }
    //   }>
    // El sort de los grupos es por el sort_order de la primera opción
    // que apareció en ese grupo (mismo criterio que ProductDetailModal).
    const optionsByItem = new Map<string, Map<string, {
      options: MenuItemOption[];
      firstSort: number;
    }>>();
    try {
      const {
        data: allOptions,
        error: optErr
      } = await supabase.from('menu_item_options').select('*').eq('restaurant_id', restaurant.id).eq('is_active', true).order('sort_order', {
        ascending: true
      });
      if (optErr) {
        console.warn('[PRINT MENU] options fetch failed — printing without options:', optErr);
      } else {
        for (const opt of allOptions || []) {
          const itemId = String((opt as MenuItemOption).menu_item_id);
          const group = (opt as MenuItemOption).option_group || '';
          if (!optionsByItem.has(itemId)) {
            optionsByItem.set(itemId, new Map());
          }
          const groupsMap = optionsByItem.get(itemId)!;
          if (!groupsMap.has(group)) {
            groupsMap.set(group, {
              options: [],
              firstSort: (opt as MenuItemOption).sort_order ?? 0
            });
          }
          groupsMap.get(group)!.options.push(opt as MenuItemOption);
        }
      }
    } catch (e) {
      console.warn('[PRINT MENU] options fetch threw — printing without options:', e);
    }
    // Helper: build flat "Name ........ ₡final" rows for all options of
    // an item. Returns { hasOptions, rowsHtml }. Drops option_group
    // headers, "Requerido", max_select, bullets, and empty names — the
    // print menu is for clients on paper, not internal modifiers UI.
    // Final price for each option = base price + price_delta (clamped ≥ 0).
    const renderOptionRows = (itemId: string, basePrice: number, variant: 'natu' | 'default'): {
      hasOptions: boolean;
      rowsHtml: string;
    } => {
      const groupsMap = optionsByItem.get(String(itemId));
      if (!groupsMap || groupsMap.size === 0) {
        return {
          hasOptions: false,
          rowsHtml: ''
        };
      }
      const sortedGroups = Array.from(groupsMap.entries()).sort((a, b) => a[1].firstSort - b[1].firstSort);
      const flatOpts: {
        name: string;
        finalPrice: number;
      }[] = [];
      for (const [, payload] of sortedGroups) {
        const opts = [...payload.options].sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        for (const o of opts) {
          const name = String(o.name || '').trim();
          if (!name) continue;
          const delta = Number(o.price_delta) || 0;
          const finalPrice = Math.max(0, basePrice + delta);
          flatOpts.push({
            name,
            finalPrice
          });
        }
      }
      if (flatOpts.length === 0) {
        return {
          hasOptions: false,
          rowsHtml: ''
        };
      }
      const rowsHtml = flatOpts.map(o => '<div class="variant-row">' + '<span class="variant-name">' + escape(o.name) + '</span>' + '<span class="variant-dots"></span>' + '<span class="variant-price">' + escape(formatCurrency(o.finalPrice)) + '</span>' + '</div>').join('');
      // `variant` lets the caller pick which CSS scope to render under;
      // markup is identical, only the surrounding container differs.
      void variant;
      return {
        hasOptions: true,
        rowsHtml
      };
    };
    // ─── NatuCafé branded variant ──────────────────────────────────
    if (isNatuCafe) {
      const sections = activeCats.map(cat => {
        const catItems = items.filter(i => i.category_id === cat.id && i.is_active);
        if (catItems.length === 0) return '';
        const rows = catItems.map(item => {
          const hasImg = !!item.image_url;
          const imgBlock = hasImg ? '<div class="thumb"><img src="' + escape(item.image_url || '') + '" alt="" /></div>' : '<div class="thumb thumb-empty"></div>';
          const desc = item.description ? '<p class="item-desc">' + escape(item.description) + '</p>' : '';
          const {
            hasOptions,
            rowsHtml
          } = renderOptionRows(String(item.id), Number(item.price) || 0, 'natu');
          // Si el producto tiene opciones, mostramos el nombre solo
          // (sin precio base a la derecha) y luego las filas variant.
          // Si no tiene opciones, mostramos nombre + precio como antes.
          const headHtml = hasOptions ? '<div class="item-head item-head-noprice">' + '<span class="item-name">' + escape(item.name) + '</span>' + '</div>' : '<div class="item-head">' + '<span class="item-name">' + escape(item.name) + '</span>' + '<span class="item-price">' + escape(formatCurrency(item.price)) + '</span>' + '</div>';
          const variantsBlock = hasOptions ? '<div class="variants">' + rowsHtml + '</div>' : '';
          return '<div class="item">' + imgBlock + '<div class="item-body">' + headHtml + desc + variantsBlock + '</div>' + '</div>';
        }).join('');
        return '<section class="category">' + '<div class="cat-head"><span class="orn">✦</span>' + '<h2>' + escape(cat.name) + '</h2>' + '<span class="orn">✦</span></div>' + '<div class="cat-grid">' + rows + '</div>' + '</section>';
      }).filter(Boolean).join('');
      const cover = (restaurant as any).cover_url || '';
      const logo = restaurant.logo_url || '';
      const css = ['* { box-sizing: border-box; -webkit-print-color-adjust: exact; print-color-adjust: exact; }', '@import url(https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700;900&family=Inter:wght@300;400;500;600;700&display=swap);', 'html, body { margin: 0; padding: 0; background: #0F0D0B; color: #F5F0EB; font-family: Inter, system-ui, sans-serif; }', '.page { max-width: 760px; margin: 0 auto; padding: 0 0 40px; background: #0F0D0B; }', '/* Hero */', '.hero { position: relative; height: 280px; overflow: hidden; background: #1A1612; }', '.hero-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; opacity: 0.55; }', '.hero-overlay { position: absolute; inset: 0; background: linear-gradient(180deg, rgba(15,13,11,0.35) 0%, rgba(15,13,11,0.55) 50%, rgba(15,13,11,1) 100%); }', '.hero-inner { position: relative; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: flex-end; padding: 24px; text-align: center; }', '.logo-wrap { width: 96px; height: 96px; border-radius: 50%; background: #0F0D0B; padding: 6px; border: 2px solid #C6A969; box-shadow: 0 8px 32px rgba(0,0,0,0.5); margin-bottom: 14px; }', '.logo-wrap img { width: 100%; height: 100%; border-radius: 50%; object-fit: cover; background: #0F0D0B; }', '.brand-name { font-family: Playfair Display, Georgia, serif; font-size: 36px; font-weight: 900; letter-spacing: 0.06em; text-transform: uppercase; color: #F5F0EB; margin: 0; line-height: 1; }', '.brand-sub { margin: 6px 0 0; font-size: 10px; letter-spacing: 0.4em; text-transform: uppercase; color: #C6A969; }', '/* Decorative divider after hero */', '.divider { display: flex; align-items: center; justify-content: center; gap: 14px; padding: 22px 24px 12px; color: #C6A969; }', '.divider-line { flex: 1; height: 1px; background: linear-gradient(90deg, transparent, #C6A969 50%, transparent); max-width: 180px; }', '.divider-mark { font-size: 14px; letter-spacing: 0.4em; text-transform: uppercase; color: #C6A969; font-family: Playfair Display, serif; }', '/* Categories */', '.category { padding: 18px 32px 6px; page-break-inside: avoid; }', '.cat-head { display: flex; align-items: center; justify-content: center; gap: 12px; margin: 0 0 18px; }', '.cat-head h2 { font-family: Playfair Display, Georgia, serif; font-size: 22px; font-weight: 700; letter-spacing: 0.18em; text-transform: uppercase; color: #C6A969; margin: 0; text-align: center; }', '.orn { color: #C6A969; font-size: 12px; opacity: 0.7; }', '.cat-grid { display: grid; grid-template-columns: 1fr; gap: 14px; }', '/* Item card */', '.item { display: grid; grid-template-columns: 64px 1fr; gap: 14px; padding: 10px 0; border-bottom: 1px solid #2A2420; page-break-inside: avoid; }', '.item:last-child { border-bottom: none; }', '.thumb { width: 64px; height: 64px; border-radius: 8px; overflow: hidden; background: #1A1612; border: 1px solid #2A2420; flex-shrink: 0; }', '.thumb img { width: 100%; height: 100%; object-fit: cover; }', '.thumb-empty { background: linear-gradient(135deg, #1A1612, #0F0D0B); }', '.item-body { min-width: 0; }', '.item-head { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; }', '.item-head-noprice { justify-content: flex-start; }', '.item-name { font-family: Playfair Display, Georgia, serif; font-size: 15px; font-weight: 700; color: #F5F0EB; letter-spacing: 0.02em; }', '.item-price { font-family: Inter, sans-serif; font-size: 14px; font-weight: 700; color: #C6A969; white-space: nowrap; letter-spacing: 0.02em; }', '.item-desc { margin: 4px 0 0; font-size: 11px; color: #8B7B6B; font-style: italic; line-height: 1.5; font-weight: 300; }', '/* Variant rows (tamaños, variaciones) */', '.variants { margin-top: 6px; padding-left: 0; }', '.variant-row { display: flex; align-items: baseline; gap: 4px; padding: 2px 0; font-family: Inter, sans-serif; font-size: 12px; color: #F5F0EB; }', '.variant-name { font-weight: 400; letter-spacing: 0.01em; }', '.variant-dots { flex: 1; border-bottom: 1px dotted #5A4A3A; transform: translateY(-3px); margin: 0 6px; min-width: 24px; }', '.variant-price { font-weight: 600; color: #C6A969; white-space: nowrap; }', '/* Footer */', '.foot { margin-top: 32px; padding: 22px 24px 8px; text-align: center; border-top: 1px solid #2A2420; }', '.foot-name { font-family: Playfair Display, serif; font-size: 14px; letter-spacing: 0.3em; text-transform: uppercase; color: #C6A969; margin: 0 0 6px; }', '.foot-meta { font-size: 9px; letter-spacing: 0.25em; text-transform: uppercase; color: #8B7B6B; margin: 0; }', '/* Toolbar (not printed) */', '.toolbar { position: fixed; top: 14px; right: 14px; display: flex; gap: 8px; z-index: 999; }', '.toolbar button { font-family: Inter, sans-serif; font-size: 13px; padding: 9px 16px; background: #C6A969; color: #0F0D0B; border: none; border-radius: 8px; cursor: pointer; font-weight: 700; letter-spacing: 0.05em; }', '.toolbar button.secondary { background: #1A1612; color: #F5F0EB; border: 1px solid #2A2420; }', '/* Print rules */', '@media print {', '  .toolbar { display: none; }', '  @page { margin: 0; size: A4; }', '  html, body { background: #0F0D0B !important; }', '  .page { max-width: 100%; }', '  .hero { height: 240px; }', '}'].join('\n');
      const heroImg = cover ? '<img class="hero-img" src="' + escape(cover) + '" alt="" />' : '';
      const logoImg = logo ? '<div class="logo-wrap"><img src="' + escape(logo) + '" alt="logo" /></div>' : '';
      const html = '<!DOCTYPE html>' + '<html lang="es">' + '<head>' + '<meta charset="utf-8" />' + '<title>Menú — ' + escape(restaurant.name) + '</title>' + T_STYLE_O + css + T_STYLE_C + '</head>' + '<body>' + '<div class="toolbar">' + '<button class="secondary" onclick="window.close()">Cerrar</button>' + '<button onclick="window.print()">Imprimir / PDF</button>' + '</div>' + '<div class="page">' + '<div class="hero">' + heroImg + '<div class="hero-overlay"></div>' + '<div class="hero-inner">' + logoImg + '<h1 class="brand-name">' + escape(restaurant.name) + '</h1>' + '<div class="brand-sub">Coffee Shop · Los Santos, Costa Rica</div>' + '</div>' + '</div>' + '<div class="divider">' + '<span class="divider-line"></span>' + '<span class="divider-mark">Menú</span>' + '<span class="divider-line"></span>' + '</div>' + (sections || '<p style="text-align:center;color:#8B7B6B;padding:40px;">No hay productos activos para imprimir.</p>') + '<div class="foot">' + '<p class="foot-name">' + escape(restaurant.name) + '</p>' + '<p class="foot-meta">' + escape(today) + '</p>' + '</div>' + '</div>' + T_SCRIPT_O + 'window.addEventListener("load", function(){ setTimeout(function(){ window.print(); }, 600); });' + T_SCRIPT_C + '</body>' + '</html>';
      const win = window.open('', '_blank', 'width=860,height=1000');
      if (!win) {
        window.alert('No se pudo abrir la ventana de impresión. Permití pop-ups para este sitio e intentá de nuevo.');
        return;
      }
      win.document.open();
      win.document.write(html);
      win.document.close();
      return;
    }
    // ─── Default variant (todos los demás restaurantes) ────────────
    const sections = activeCats.map(cat => {
      const catItems = items.filter(i => i.category_id === cat.id && i.is_active);
      if (catItems.length === 0) return '';
      const rows = catItems.map(item => {
        const desc = item.description ? '<p class="item-desc">' + escape(item.description) + '</p>' : '';
        const {
          hasOptions,
          rowsHtml
        } = renderOptionRows(String(item.id), Number(item.price) || 0, 'default');
        // Si tiene opciones: nombre solo + filas "Nombre .... ₡Final".
        // Si no tiene opciones: layout clásico nombre ... precio.
        if (hasOptions) {
          return '<div class="item item-with-variants">' + '<div class="item-head item-head-noprice">' + '<span class="item-name">' + escape(item.name) + '</span>' + '</div>' + desc + '<div class="variants">' + rowsHtml + '</div>' + '</div>';
        }
        return '<div class="item">' + '<div class="item-head">' + '<span class="item-name">' + escape(item.name) + '</span>' + '<span class="dots"></span>' + '<span class="item-price">' + escape(formatCurrency(item.price)) + '</span>' + '</div>' + desc + '</div>';
      }).join('');
      return '<section class="category"><h2>' + escape(cat.name) + '</h2>' + rows + '</section>';
    }).filter(Boolean).join('');
    const css = ['* { box-sizing: border-box; }', 'html, body { margin: 0; padding: 0; background: #fff; color: #1a1a1a; font-family: Georgia, Times New Roman, serif; }', '.page { max-width: 720px; margin: 0 auto; padding: 32px 40px 48px; }', 'header { text-align: center; border-bottom: 2px solid #1a1a1a; padding-bottom: 16px; margin-bottom: 32px; }', 'header h1 { margin: 0 0 4px; font-size: 32px; letter-spacing: 0.04em; text-transform: uppercase; }', 'header .sub { font-size: 12px; color: #666; letter-spacing: 0.16em; text-transform: uppercase; }', '.category { margin-bottom: 28px; page-break-inside: avoid; }', '.category h2 { font-size: 18px; margin: 0 0 12px; padding-bottom: 6px; border-bottom: 1px solid #d4a017; color: #8b6f1a; text-transform: uppercase; letter-spacing: 0.12em; }', '.item { margin-bottom: 14px; page-break-inside: avoid; }', '.item-with-variants { margin-bottom: 18px; }', '.item-head { display: flex; align-items: baseline; gap: 6px; }', '.item-head-noprice { justify-content: flex-start; }', '.item-name { font-weight: 700; font-size: 14px; }', '.dots { flex: 1; border-bottom: 1px dotted #999; transform: translateY(-3px); margin: 0 4px; }', '.item-price { font-weight: 700; font-size: 14px; white-space: nowrap; }', '.item-desc { margin: 2px 0 0; font-size: 12px; color: #555; font-style: italic; line-height: 1.4; }', '/* Variant rows (tamaños, variaciones) — Pequeño .... ₡1000 */', '.variants { margin: 4px 0 0 16px; }', '.variant-row { display: flex; align-items: baseline; gap: 4px; padding: 2px 0; font-family: Georgia, Times New Roman, serif; font-size: 13px; color: #333; }', '.variant-name { font-weight: 500; }', '.variant-dots { flex: 1; border-bottom: 1px dotted #999; transform: translateY(-3px); margin: 0 6px; min-width: 24px; }', '.variant-price { font-weight: 700; color: #1a1a1a; white-space: nowrap; }', 'footer { margin-top: 40px; text-align: center; font-size: 10px; color: #999; border-top: 1px solid #eee; padding-top: 12px; }', '.toolbar { position: fixed; top: 12px; right: 12px; display: flex; gap: 8px; }', '.toolbar button { font-family: inherit; font-size: 13px; padding: 8px 14px; background: #1a1a1a; color: #fff; border: none; border-radius: 6px; cursor: pointer; }', '.toolbar button.secondary { background: #fff; color: #1a1a1a; border: 1px solid #ccc; }', '@media print { .toolbar { display: none; } .page { padding: 16px 24px; } @page { margin: 14mm; } }'].join('\n');
    const html = '<!DOCTYPE html>' + '<html lang="es">' + '<head>' + '<meta charset="utf-8" />' + '<title>Menú — ' + escape(restaurant.name) + '</title>' + T_STYLE_O + css + T_STYLE_C + '</head>' + '<body>' + '<div class="toolbar">' + '<button class="secondary" onclick="window.close()">Cerrar</button>' + '<button onclick="window.print()">Imprimir / PDF</button>' + '</div>' + '<div class="page">' + '<header><h1>' + escape(restaurant.name) + '</h1><div class="sub">Menú · ' + escape(today) + '</div></header>' + (sections || '<p style="text-align:center;color:#999;">No hay productos activos para imprimir.</p>') + '<footer>' + escape(restaurant.name) + ' · Generado el ' + escape(today) + '</footer>' + '</div>' + T_SCRIPT_O + 'window.addEventListener("load", function(){ setTimeout(function(){ window.print(); }, 250); });' + T_SCRIPT_C + '</body>' + '</html>';
    const win = window.open('', '_blank', 'width=820,height=900');
    if (!win) {
      window.alert('No se pudo abrir la ventana de impresión. Permití pop-ups para este sitio e intentá de nuevo.');
      return;
    }
    win.document.open();
    win.document.write(html);
    win.document.close();
  };
  return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5071">
      <div className="p-4 sm:p-6 max-w-7xl mx-auto space-y-6" data-id="element-5072">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4" data-id="element-5073">
          <div data-id="element-5074">
            <h1 className="text-2xl font-bold text-gray-900" data-id="element-5075">
              Gestión de Menú
            </h1>
            <p className="text-gray-500" data-id="element-5076">
              Administra categorías, productos y opciones
            </p>
          </div>
          <div className="flex gap-2 flex-wrap" data-id="element-5077">
            <Button variant="secondary" onClick={handlePrintMenu} data-id="element-5078">
              <Printer className="w-4 h-4 mr-2" data-id="element-5079" />
              Imprimir Menú
            </Button>
            <Button variant="secondary" onClick={() => setIsCatReorderDrawerOpen(true)} data-id="element-5080">
              <FolderTree className="w-4 h-4 mr-2" data-id="element-5081" />
              Ordenar Categorías
            </Button>
            <Button variant="secondary" onClick={() => setIsReorderDrawerOpen(true)} data-id="element-5082">
              <ListOrdered className="w-4 h-4 mr-2" data-id="element-5083" />
              Ordenar Productos
            </Button>
            <Button variant={activeTab === 'categories' ? 'primary' : 'secondary'} onClick={() => setActiveTab('categories')} data-id="element-5084">
              <Layers className="w-4 h-4 mr-2" data-id="element-5085" />
              Categorías
            </Button>
            <Button variant={activeTab === 'items' ? 'primary' : 'secondary'} onClick={() => setActiveTab('items')} data-id="element-5086">
              <UtensilsCrossed className="w-4 h-4 mr-2" data-id="element-5087" />
              Productos
            </Button>
          </div>
        </div>

        {activeTab === 'categories' && <div className="space-y-4" data-id="element-5088">
            <div className="flex justify-end" data-id="element-5089">
              <Button onClick={() => handleOpenCatModal()} data-id="element-5090">
                <Plus className="w-4 h-4 mr-2" data-id="element-5091" />
                Nueva Categoría
              </Button>
            </div>

            {catLoading ? <div className="space-y-3" data-id="element-5092">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-16 w-full rounded-xl" data-id="element-5093" />)}
              </div> : categories.length === 0 ? <EmptyState icon={Layers} title="No hay categorías" description="Crea tu primera categoría para empezar a organizar tu menú." actionLabel="Crear Categoría" onAction={() => handleOpenCatModal()} data-id="element-5094" /> : <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-5095">
                <ul className="divide-y divide-gray-100" data-id="element-5096">
                  {categories.map(cat => <li key={cat.id} className="p-4 flex items-center justify-between hover:bg-gray-50 transition-colors" data-id="element-5097">
                      <div className="flex items-center gap-4" data-id="element-5098">
                        <GripVertical className="w-5 h-5 text-gray-300 cursor-grab" data-id="element-5099" />
                        <div data-id="element-5100">
                          <h3 className="font-bold text-gray-900" data-id="element-5101">
                            {cat.name}
                          </h3>
                          {cat.description && <p className="text-sm text-gray-500" data-id="element-5102">
                              {cat.description}
                            </p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-3" data-id="element-5103">
                        <Badge variant={cat.is_active ? 'success' : 'default'} className="cursor-pointer" onClick={() => toggleCategory(cat.id, !cat.is_active)} data-id="element-5104">
                          {cat.is_active ? 'Activa' : 'Inactiva'}
                        </Badge>
                        <button onClick={() => handleOpenCatModal(cat)} className="p-2 text-gray-400 hover:text-brand-amber transition-colors" data-id="element-5105">
                          <Edit2 className="w-4 h-4" data-id="element-5106" />
                        </button>
                      </div>
                    </li>)}
                </ul>
              </div>}
          </div>}

        {activeTab === 'items' && <div className="space-y-6" data-id="element-5107">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center gap-3" data-id="element-5108">
              <div className="relative flex-1 max-w-md" data-id="element-5109">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-5110" />
                <input type="text" placeholder="Buscar producto..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber/50 focus:border-brand-amber bg-white" data-id="element-5111" />
                {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-id="element-5112">
                    <X className="w-4 h-4" data-id="element-5113" />
                  </button>}
              </div>
              <select value={filterCategoryId} onChange={e => setFilterCategoryId(e.target.value === '' ? '' : Number(e.target.value))} className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber/50 focus:border-brand-amber bg-white text-gray-700 min-w-[180px]" data-id="element-5114">
                <option value="" data-id="element-5115">Todas las categorías</option>
                {categories.map(cat => <option key={cat.id} value={cat.id} data-id="element-5116">
                    {cat.name}
                  </option>)}
              </select>
              <Button onClick={() => handleOpenItemModal()} data-id="element-5117">
                <Plus className="w-4 h-4 mr-2" data-id="element-5118" />
                Nuevo Producto
              </Button>
            </div>

            {itemsLoading || catLoading ? <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5119">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-32 w-full rounded-xl" data-id="element-5120" />)}
              </div> : items.length === 0 ? <EmptyState icon={UtensilsCrossed} title="No hay productos" description="Agrega productos a tu menú para que los clientes puedan ordenar." actionLabel="Crear Producto" onAction={() => handleOpenItemModal()} data-id="element-5121" /> : <div className="space-y-8" data-id="element-5122">
                {categories.filter(cat => !filterCategoryId || cat.id === filterCategoryId).map(cat => {
            const catItems = items.filter(i => i.category_id === cat.id && (!searchQuery || i.name.toLowerCase().includes(searchQuery.toLowerCase()) || i.description?.toLowerCase().includes(searchQuery.toLowerCase())));
            if (catItems.length === 0) return null;
            return <div key={cat.id} className="space-y-4" data-id="element-5123">
                        <h2 className="text-lg font-bold text-gray-900 border-b border-gray-200 pb-2" data-id="element-5124">
                          {cat.name}
                        </h2>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-5125">
                          {catItems.map(item => <div key={item.id} className={cn('bg-white rounded-xl border p-4 flex flex-col justify-between transition-all', item.is_active ? 'border-gray-200 shadow-sm' : 'border-gray-200 opacity-60 bg-gray-50')} data-id="element-5126">
                              <div className="flex justify-between items-start mb-2" data-id="element-5127">
                                <div data-id="element-5128">
                                  <h3 className="font-bold text-gray-900 text-lg" data-id="element-5129">
                                    {item.name}
                                  </h3>
                                  <p className="text-brand-amber font-bold" data-id="element-5130">
                                    {formatCurrency(item.price)}
                                  </p>
                                </div>
                                <div className="flex items-center gap-2" data-id="element-5131">
                                  {item.station_code ? <Badge variant="default" className="text-[10px] uppercase" data-id="element-5132">
                                      {item.station_code}
                                    </Badge> : <Badge variant="warning" className="text-[10px]" data-id="element-5133">
                                      ⚠️ Sin estación
                                    </Badge>}
                                  <Badge variant={item.is_active ? 'success' : 'default'} className={cn('cursor-pointer', !item.station_code && item.is_active && 'opacity-50')} onClick={() => {
                        if (!item.station_code && !item.is_active) {
                          alert('Debes asignar una estación de preparación antes de activar este producto.');
                          return;
                        }
                        toggleItem(item.id, !item.is_active);
                      }} data-id="element-5134">
                                    {item.is_active ? 'Activo' : 'Inactivo'}
                                  </Badge>
                                </div>
                              </div>
                              {item.description && <p className="text-sm text-gray-500 mb-4 line-clamp-2" data-id="element-5135">
                                  {item.description}
                                </p>}
                              <div className="flex justify-end gap-2 mt-auto pt-4 border-t border-gray-100" data-id="element-5136">
                                <Button variant="secondary" size="sm" onClick={() => handleOpenOptionsModal(item)} data-id="element-5137">
                                  <Settings2 className="w-4 h-4 mr-1.5" data-id="element-5138" />
                                  Opciones
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handleOpenItemModal(item)} data-id="element-5139">
                                  <Edit2 className="w-4 h-4 mr-1.5" data-id="element-5140" />
                                  Editar
                                </Button>
                              </div>
                            </div>)}
                        </div>
                      </div>;
          })}
              </div>}
          </div>}
      </div>

      {/* Modals */}
      <CategoryModal isOpen={isCatModalOpen} onClose={() => setIsCatModalOpen(false)} category={editingCat} onSave={async data => {
      let result;
      if (editingCat) {
        result = await updateCategory(editingCat.id, data);
      } else {
        result = await createCategory(data);
      }
      if (result?.success) {
        setIsCatModalOpen(false);
      }
      return result;
    }} data-id="element-5141" />

      <ItemModal isOpen={isItemModalOpen} onClose={() => setIsItemModalOpen(false)} item={editingItem} categories={categories} stations={stations} onSave={async data => {
      let result;
      if (editingItem) {
        result = await updateItem(editingItem.id, data);
      } else {
        result = await createItem(data as any);
      }
      if (result?.success) {
        setIsItemModalOpen(false);
      }
      return result;
    }} onDelete={async () => {
      if (editingItem) {
        if (confirm('¿Estás seguro de eliminar este producto?')) {
          const result = await deleteItem(editingItem.id);
          if (result?.success) {
            setIsItemModalOpen(false);
          }
          return result;
        }
      }
      return {
        success: false
      };
    }} data-id="element-5142" />

      {managingOptionsForItem && <OptionsModal isOpen={isOptionsModalOpen} onClose={() => setIsOptionsModalOpen(false)} item={managingOptionsForItem} restaurantId={restaurant.id} data-id="element-5143" />}

      <MenuReorderDrawer restaurantId={restaurant.id} isOpen={isReorderDrawerOpen} onClose={() => setIsReorderDrawerOpen(false)} data-id="element-5144" />

      <CategoryReorderDrawer restaurantId={restaurant.id} slug={slug} isOpen={isCatReorderDrawerOpen} onClose={() => setIsCatReorderDrawerOpen(false)} data-id="element-5145" />
    </AdminLayout>;
}
// ─── Modals ──────────────────────────────────────────────────
function CategoryModal({
  isOpen,
  onClose,
  category,
  onSave
}: {
  isOpen: boolean;
  onClose: () => void;
  category: MenuCategory | null;
  onSave: (data: any) => Promise<any>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (isOpen) {
      setName(category?.name || '');
      setDescription(category?.description || '');
      setIsActive(category?.is_active ?? true);
      setError(null);
    }
  }, [isOpen, category]);
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsSubmitting(true);
    console.log('[NOIZA][UI] CategoryModal handleSubmit called with:', {
      name,
      description,
      is_active: isActive
    });
    try {
      const result = await onSave({
        name,
        description,
        is_active: isActive
      });
      console.log('[NOIZA][UI] CategoryModal onSave result:', result);
      if (result && !result.success) {
        setError(result.error || 'Error al guardar la categoría');
      }
    } catch (err: any) {
      console.error('[NOIZA][UI] CategoryModal onSave EXCEPTION:', err);
      setError(err?.message || 'Error inesperado al guardar');
    }
    setIsSubmitting(false);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={category ? 'Editar Categoría' : 'Nueva Categoría'} data-id="element-5146">
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-5147">
        <Input label="Nombre" value={name} onChange={e => setName(e.target.value)} required placeholder="Ej: Entradas, Bebidas, Postres" data-id="element-5148" />
        <Textarea label="Descripción (opcional)" value={description} onChange={e => setDescription(e.target.value)} placeholder="Breve descripción de la categoría" data-id="element-5149" />
        <div className="flex items-center gap-2 pt-2" data-id="element-5150">
          <input type="checkbox" id="cat-active" checked={isActive} onChange={e => setIsActive(e.target.checked)} className="w-4 h-4 text-brand-amber rounded border-gray-300 focus:ring-brand-amber" data-id="element-5151" />
          <label htmlFor="cat-active" className="text-sm font-medium text-gray-700" data-id="element-5152">
            Categoría activa (visible para clientes)
          </label>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-id="element-5153">
            {error}
          </div>}
        <div className="pt-4 flex justify-end gap-3" data-id="element-5154">
          <Button type="button" variant="ghost" onClick={onClose} data-id="element-5155">
            Cancelar
          </Button>
          <Button type="submit" isLoading={isSubmitting} data-id="element-5156">
            Guardar
          </Button>
        </div>
      </form>
    </Modal>;
}
function ItemModal({
  isOpen,
  onClose,
  item,
  categories,
  stations,
  onSave,
  onDelete
}: {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem | null;
  categories: MenuCategory[];
  stations: any[];
  onSave: (data: any) => Promise<any>;
  onDelete: () => Promise<any>;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [stationCode, setStationCode] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isShareable, setIsShareable] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [existingImageUrl, setExistingImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const imageInputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOpen) {
      setName(item?.name || '');
      setDescription(item?.description || '');
      setPrice(item?.price?.toString() || '');
      setCategoryId(item?.category_id || (categories[0]?.id ?? ''));
      setStationCode(item?.station_code || '');
      setIsActive(item?.is_active ?? true);
      setIsShareable((item as any)?.is_shareable ?? false);
      setError(null);
      setImageFile(null);
      setImagePreview(null);
      setExistingImageUrl(item?.image_url || null);
      setUploadingImage(false);
    }
  }, [isOpen, item, categories]);
  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setError('Solo se permiten imágenes JPG, PNG o WebP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError('La imagen no puede superar 5MB');
      return;
    }
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
    setExistingImageUrl(null);
    setError(null);
  };
  const removeImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImageFile(null);
    setImagePreview(null);
    setExistingImageUrl(null);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    // Validate station_code is required if product is active AND stations exist
    if (isActive && !stationCode && stations.length > 0) {
      setError('Debes asignar una estación de preparación para activar este producto.');
      return;
    }
    setIsSubmitting(true);
    // Upload image if a new file was selected
    let imageUrl: string | null = existingImageUrl;
    if (imageFile) {
      setUploadingImage(true);
      try {
        const ext = imageFile.name.split('.').pop() || 'jpg';
        const safeName = name.toLowerCase().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') || 'product';
        const path = `restaurant-${item?.restaurant_id || 'new'}/${Date.now()}-${safeName}.${ext}`;
        const {
          error: uploadError
        } = await supabase.storage.from('menu-images').upload(path, imageFile, {
          upsert: true,
          contentType: imageFile.type
        });
        if (uploadError) {
          console.error('[MenuMgmt] Image upload error:', uploadError);
          setError(`Error al subir imagen: ${uploadError.message}`);
          setIsSubmitting(false);
          setUploadingImage(false);
          return;
        }
        const {
          data: urlData
        } = supabase.storage.from('menu-images').getPublicUrl(path);
        imageUrl = urlData.publicUrl;
      } catch (err: any) {
        setError(`Error al subir imagen: ${err?.message || 'Error desconocido'}`);
        setIsSubmitting(false);
        setUploadingImage(false);
        return;
      }
      setUploadingImage(false);
    }
    const payload = {
      name,
      description,
      price: parseFloat(price),
      category_id: categoryId,
      station_code: stationCode || null,
      is_active: isActive,
      is_shareable: isShareable,
      image_url: imageUrl
    };
    console.log('[NOIZA][UI] ItemModal handleSubmit called with:', payload);
    try {
      const result = await onSave(payload);
      console.log('[NOIZA][UI] ItemModal onSave result:', result);
      if (result && !result.success) {
        setError(result.error || 'Error al guardar el producto');
      }
    } catch (err: any) {
      console.error('[NOIZA][UI] ItemModal onSave EXCEPTION:', err);
      setError(err?.message || 'Error inesperado al guardar');
    }
    setIsSubmitting(false);
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={item ? 'Editar Producto' : 'Nuevo Producto'} data-id="element-5157">
      <form onSubmit={handleSubmit} className="space-y-4" data-id="element-5158">
        <Input label="Nombre del producto" value={name} onChange={e => setName(e.target.value)} required data-id="element-5159" />

        {/* Image Upload */}
        <div data-id="element-5160">
          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5161">
            Foto del producto
          </label>
          {imagePreview || existingImageUrl ? <div className="relative w-full h-40 rounded-xl overflow-hidden border border-gray-200 bg-gray-50" data-id="element-5162">
              <img src={imagePreview || existingImageUrl || ''} alt="Preview" className="w-full h-full object-cover" data-id="element-5163" />
              <button type="button" onClick={removeImage} className="absolute top-2 right-2 w-7 h-7 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors" data-id="element-5164">
                <X className="w-4 h-4" data-id="element-5165" />
              </button>
              <button type="button" onClick={() => imageInputRef.current?.click()} className="absolute bottom-2 right-2 px-3 py-1.5 bg-black/60 hover:bg-black/80 text-white text-xs font-medium rounded-lg transition-colors" data-id="element-5166">
                Cambiar
              </button>
            </div> : <button type="button" onClick={() => imageInputRef.current?.click()} className="w-full h-32 border-2 border-dashed border-gray-300 rounded-xl flex flex-col items-center justify-center gap-2 text-gray-400 hover:border-gray-400 hover:text-gray-500 hover:bg-gray-50 transition-all cursor-pointer" data-id="element-5167">
              <Upload className="w-6 h-6" data-id="element-5168" />
              <span className="text-sm font-medium" data-id="element-5169">Subir foto</span>
              <span className="text-xs" data-id="element-5170">PNG, JPG o WebP (máx 5MB)</span>
            </button>}
          <input ref={imageInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleImageSelect} className="hidden" data-id="element-5171" />
        </div>

        <div className="grid grid-cols-2 gap-4" data-id="element-5172">
          <Input label="Precio" type="number" min="0" step="0.01" value={price} onChange={e => setPrice(e.target.value)} required data-id="element-5173" />
          <div data-id="element-5174">
            <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5175">
              Categoría
            </label>
            <select value={categoryId} onChange={e => setCategoryId(e.target.value)} required className="w-full h-11 px-3 py-2 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber" data-id="element-5176">
              <option value="" disabled data-id="element-5177">
                Seleccionar...
              </option>
              {categories.map(c => <option key={c.id} value={c.id} data-id="element-5178">
                  {c.name}
                </option>)}
            </select>
          </div>
        </div>

        <div data-id="element-5179">
          <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-5180">
            Estación de preparación (KDS){' '}
            {isActive && stations.length > 0 && <span className="text-red-500" data-id="element-5181">*</span>}
          </label>
          {stations.length > 0 ? <select value={stationCode} onChange={e => setStationCode(e.target.value)} className={cn('w-full h-11 px-3 py-2 rounded-xl border bg-white text-sm focus:outline-none focus:ring-2 focus:ring-brand-amber', isActive && !stationCode && stations.length > 0 ? 'border-red-300 bg-red-50' : 'border-gray-200')} data-id="element-5182">
              <option value="" data-id="element-5183">Seleccionar estación...</option>
              {stations.map(s => <option key={s.id} value={s.code} data-id="element-5184">
                  {s.name}
                </option>)}
            </select> : <div className="w-full h-11 px-3 py-2 rounded-xl border border-gray-200 bg-gray-50 text-sm text-gray-400 flex items-center" data-id="element-5185">
              No hay estaciones configuradas — se omitirá
            </div>}
          <p className={cn('text-xs mt-1', isActive && !stationCode && stations.length > 0 ? 'text-red-600 font-medium' : 'text-gray-500')} data-id="element-5186">
            {stations.length === 0 ? 'No se requiere estación. Puedes configurar estaciones desde el panel de administración.' : isActive && !stationCode ? '⚠️ Requerido para productos activos. Determina en qué pantalla de cocina aparecerá.' : 'Determina en qué pantalla de cocina aparecerá este producto.'}
          </p>
        </div>

        <Textarea label="Descripción" value={description} onChange={e => setDescription(e.target.value)} data-id="element-5187" />

        <div className="flex flex-col gap-3 pt-2" data-id="element-5188">
          <div className="flex items-center gap-2" data-id="element-5189">
            <input type="checkbox" id="item-active" checked={isActive} onChange={e => {
            const newActive = e.target.checked;
            if (newActive && !stationCode && stations.length > 0) {
              setError('Debes asignar una estación de preparación antes de activar este producto.');
              return;
            }
            setIsActive(newActive);
            setError(null);
          }} className="w-4 h-4 text-brand-amber rounded border-gray-300 focus:ring-brand-amber" data-id="element-5190" />
            <label htmlFor="item-active" className="text-sm font-medium text-gray-700" data-id="element-5191">
              Producto activo (visible para clientes)
            </label>
          </div>

          <div className="flex items-center gap-2" data-id="element-5192">
            <input type="checkbox" id="item-shareable" checked={isShareable} onChange={e => setIsShareable(e.target.checked)} className="w-4 h-4 text-brand-amber rounded border-gray-300 focus:ring-brand-amber" data-id="element-5193" />
            <label htmlFor="item-shareable" className="text-sm font-medium text-gray-700" data-id="element-5194">
              Compartible / Divisible en caja
            </label>
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-3" data-id="element-5195">
            {error}
          </div>}
        <div className="pt-4 flex justify-between items-center" data-id="element-5196">
          {item ? <Button type="button" variant="danger" onClick={onDelete} data-id="element-5197">
              Eliminar
            </Button> : <div data-id="element-5198" />}
          <div className="flex gap-3" data-id="element-5199">
            <Button type="button" variant="ghost" onClick={onClose} data-id="element-5200">
              Cancelar
            </Button>
            <Button type="submit" isLoading={isSubmitting} data-id="element-5201">
              Guardar
            </Button>
          </div>
        </div>
      </form>
    </Modal>;
}
function OptionsModal({
  isOpen,
  onClose,
  item,
  restaurantId
}: {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem;
  restaurantId: string;
}) {
  const {
    options,
    loading,
    createOption,
    updateOption,
    deleteOption
  } = useMenuItemOptions(item.id);
  const [name, setName] = useState('');
  const [priceDelta, setPriceDelta] = useState('');
  const [group, setGroup] = useState('Opciones');
  const [isSubmitting, setIsSubmitting] = useState(false);
  // ─── Edición inline de una opción existente ───────────────────────
  // El cajero/admin puede editar nombre, grupo y precio de una opción
  // ya creada SIN borrarla y recrearla. updateOption hace UPDATE sobre
  // la fila existente (mismo id) — no rompe referencias.
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editGroup, setEditGroup] = useState('');
  const [editPrice, setEditPrice] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const startEdit = (opt: MenuItemOption) => {
    setEditingId(opt.id);
    setEditName(opt.name);
    setEditGroup(opt.option_group);
    setEditPrice(String(opt.price_delta ?? 0));
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditGroup('');
    setEditPrice('');
  };
  const saveEdit = async (id: string) => {
    const trimmedName = editName.trim();
    const trimmedGroup = editGroup.trim();
    if (!trimmedName) {
      window.alert('Escribí un nombre para la opción.');
      return;
    }
    if (!trimmedGroup) {
      window.alert('Escribí un grupo (ej: Acompañamiento, Salsa, etc.).');
      return;
    }
    setSavingEdit(true);
    const res = await updateOption(id, {
      name: trimmedName,
      option_group: trimmedGroup,
      price_delta: parseFloat(editPrice) || 0
    });
    setSavingEdit(false);
    if (!res.success) {
      window.alert(`No se pudo actualizar la opción: ${res.error || 'Error desconocido'}`);
      return;
    }
    cancelEdit();
  };
  // ─── Controles por GRUPO de opciones ──────────────────────────────
  // Todas las opciones de un mismo option_group comparten max_select e
  // is_required (así lo consume ProductDetailModal). Estos controles
  // dejan que el admin defina, desde el KDS/gestión, cuántas opciones
  // puede elegir el cliente (1, 2, 3… o Todas) y si el grupo es
  // obligatorio — aplicando el cambio a TODAS las filas del grupo.
  const [savingGroup, setSavingGroup] = useState<string | null>(null);
  const applyGroupSetting = async (groupName: string, patch: Partial<{
    max_select: number;
    is_required: boolean;
  }>) => {
    const groupOpts = options.filter(o => o.option_group === groupName);
    if (groupOpts.length === 0) return;
    setSavingGroup(groupName);
    let firstError: string | null = null;
    for (const opt of groupOpts) {
      const res = await updateOption(opt.id, patch);
      if (!res.success && !firstError) firstError = res.error || 'Error';
    }
    setSavingGroup(null);
    if (firstError) {
      window.alert(`No se pudo actualizar el grupo: ${firstError}`);
    }
  };
  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedGroup = group.trim();
    if (!trimmedName) {
      window.alert('Escribí un nombre para la opción.');
      return;
    }
    if (!trimmedGroup) {
      window.alert('Escribí un grupo (ej: Acompañamiento, Salsa, etc.).');
      return;
    }
    setIsSubmitting(true);
    const res = await createOption({
      restaurant_id: restaurantId,
      option_group: trimmedGroup,
      name: trimmedName,
      price_delta: parseFloat(priceDelta) || 0
    });
    setIsSubmitting(false);
    if (!res.success) {
      // Surface the real reason instead of silently clearing the form
      // and pretending it worked. Most common causes: RLS INSERT
      // policy missing on `menu_item_options`, or a NOT NULL column
      // we didn't fill (rare, the hook already provides defaults).
      window.alert(`No se pudo guardar la opción: ${res.error || 'Error desconocido'}\n\nSi dice "row-level security" o "permission denied", corré FIX_MENU_ITEM_OPTIONS_CRUD.sql en Supabase.`);
      // Keep the form values so the user doesn't have to retype.
      return;
    }
    setName('');
    setPriceDelta('');
  };
  return <Modal isOpen={isOpen} onClose={onClose} title={`Opciones: ${item.name}`} data-id="element-5202">
      <div className="space-y-6" data-id="element-5203">
        {/* List existing options */}
        {loading ? <Skeleton className="h-20 w-full rounded-xl" data-id="element-5204" /> : options.length > 0 ? <div className="space-y-4" data-id="element-5205">
            {Object.entries(options.reduce((acc, o) => {
          const g = o.option_group || 'Opciones';
          if (!acc[g]) acc[g] = [];
          acc[g].push(o);
          return acc;
        }, {} as Record<string, MenuItemOption[]>)).map(([groupName, groupOpts]) => {
          const groupCount = groupOpts.length;
          const rawMax = groupOpts[0]?.max_select && groupOpts[0].max_select > 0 ? groupOpts[0].max_select : 1;
          const groupMax = Math.min(rawMax, groupCount);
          const groupRequired = !!groupOpts[0]?.is_required;
          const isSavingThisGroup = savingGroup === groupName;
          return <div key={groupName} className="bg-gray-50 rounded-xl border border-gray-200 overflow-hidden" data-id="element-5206">
                  {/* Group controls header */}
                  <div className="p-3 border-b border-gray-200 bg-white" data-id="element-5207">
                    <p className="font-bold text-sm text-gray-900 mb-2" data-id="element-5208">
                      {groupName}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2" data-id="element-5209">
                      <label className="flex items-center gap-2 text-xs font-semibold text-gray-700" data-id="element-5210">
                        ¿Cuántas puede elegir el cliente?
                        <select value={groupMax} disabled={isSavingThisGroup} onChange={e => applyGroupSetting(groupName, {
                    max_select: parseInt(e.target.value, 10)
                  })} className="border border-gray-300 rounded-md px-2 py-1 text-xs font-bold text-gray-900 bg-white focus:outline-none focus:ring-1 focus:ring-amber-500 disabled:opacity-50" data-id="element-5211">
                          {Array.from({
                      length: groupCount
                    }, (_, i) => i + 1).map(n => <option key={n} value={n} data-id="element-5212">
                              {n === groupCount && groupCount > 1 ? `Todas (${n})` : n}
                            </option>)}
                        </select>
                      </label>
                      <label className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 cursor-pointer" data-id="element-5213">
                        <input type="checkbox" checked={groupRequired} disabled={isSavingThisGroup} onChange={e => applyGroupSetting(groupName, {
                    is_required: e.target.checked
                  })} className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500" data-id="element-5214" />
                        Obligatorio
                      </label>
                      {isSavingThisGroup && <span className="text-[11px] text-amber-600 font-bold" data-id="element-5215">
                          Guardando…
                        </span>}
                    </div>
                  </div>
                  <ul className="divide-y divide-gray-200" data-id="element-5216">
                    {groupOpts.map(opt => {
                const isEditing = editingId === opt.id;
                if (isEditing) {
                  return <li key={opt.id} className="p-3 bg-amber-50/50" data-id="element-5217">
                            <div className="grid grid-cols-2 gap-2 mb-2" data-id="element-5218">
                              <Input placeholder="Nombre" value={editName} onChange={e => setEditName(e.target.value)} data-id="element-5219" />
                              <Input type="number" step="0.01" placeholder="Precio extra" value={editPrice} onChange={e => setEditPrice(e.target.value)} data-id="element-5220" />
                            </div>
                            <Input placeholder="Grupo" value={editGroup} onChange={e => setEditGroup(e.target.value)} data-id="element-5221" />
                            <div className="flex items-center justify-end gap-2 mt-2" data-id="element-5222">
                              <Button type="button" variant="secondary" size="sm" onClick={cancelEdit} disabled={savingEdit} data-id="element-5223">
                                Cancelar
                              </Button>
                              <Button type="button" size="sm" isLoading={savingEdit} onClick={() => saveEdit(opt.id)} data-id="element-5224">
                                <span className="flex items-center gap-1.5" data-id="element-5225">
                                  <Check className="w-4 h-4" data-id="element-5226" />
                                  Guardar
                                </span>
                              </Button>
                            </div>
                          </li>;
                }
                return <li key={opt.id} className="p-3 flex justify-between items-center" data-id="element-5227">
                          <div data-id="element-5228">
                            <p className="font-medium text-sm text-gray-900" data-id="element-5229">
                              {opt.name}
                            </p>
                            <p className="text-xs text-gray-500" data-id="element-5230">
                              Grupo: {opt.option_group}
                            </p>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-5231">
                            <span className="text-sm font-bold text-gray-900" data-id="element-5232">
                              {opt.price_delta > 0 ? '+' : ''}
                              {formatCurrency(opt.price_delta)}
                            </span>
                            <button onClick={() => startEdit(opt)} className="p-1.5 text-gray-400 hover:text-amber-600 hover:bg-amber-50 rounded-md transition-colors" aria-label={`Editar ${opt.name}`} data-id="element-5233">
                              <Edit2 className="w-4 h-4" data-id="element-5234" />
                            </button>
                            <button onClick={async () => {
                      if (!window.confirm(`¿Eliminar la opción "${opt.name}"? Esta acción no se puede deshacer.`)) return;
                      const res = await deleteOption(opt.id);
                      if (!res.success) {
                        // Surface the real reason so the user (or
                        // support) can act on it instead of silently
                        // failing. Common causes: RLS DELETE policy
                        // missing on menu_item_options, or an FK
                        // constraint from order_item_options pointing
                        // at this row.
                        window.alert(`No se pudo eliminar la opción: ${res.error || 'Error desconocido'}`);
                      }
                    }} className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors" aria-label={`Eliminar ${opt.name}`} data-id="element-5235">
                              <Trash2 className="w-4 h-4" data-id="element-5236" />
                            </button>
                          </div>
                        </li>;
              })}
                  </ul>
                </div>;
        })}
          </div> : <p className="text-sm text-gray-500 text-center py-4" data-id="element-5237">
            Este producto no tiene opciones adicionales.
          </p>}

        {/* Add new option form */}
        <form onSubmit={handleAdd} className="bg-white p-4 rounded-xl border border-gray-200 space-y-3" data-id="element-5238">
          <h4 className="font-bold text-sm text-gray-900" data-id="element-5239">Agregar Opción</h4>
          <div className="grid grid-cols-2 gap-3" data-id="element-5240">
            <Input placeholder="Nombre (ej: Extra Queso)" value={name} onChange={e => setName(e.target.value)} required data-id="element-5241" />
            <Input type="number" step="0.01" placeholder="Precio extra (ej: 500)" value={priceDelta} onChange={e => setPriceDelta(e.target.value)} required data-id="element-5242" />
          </div>
          <Input placeholder="Grupo (ej: Extras, Tamaños)" value={group} onChange={e => setGroup(e.target.value)} required data-id="element-5243" />
          <Button type="submit" fullWidth isLoading={isSubmitting} size="sm" data-id="element-5244">
            Agregar Opción
          </Button>
        </form>
      </div>
    </Modal>;
}